import React, { Fragment, useState } from 'react';

import { FormTitle } from '@components/Forms/FormTitle';
import { ManagementFieldDisplay as OldManagementFieldDisplay } from '@components/ManagementFieldDisplay';
import { NotificationItem } from '@components/NotificationItem';
import { BookingInquiryCargosDisplay } from '@components/utilities/BookingInquiry/BookingInquiryCargosDisplay';
import { ContactDisplay } from '@components/utilities/BookingInquiry/ContactDisplay';
import { RoutePlanLineDisplay } from '@components/utilities/BookingInquiry/RoutePlanLineDisplay';
import { bookingInquiryStrings, bookingInquiryTradeConditionStrings } from '@data/bookingInquiry';
import {
  bookingInquiryAdminGroupNameToFields,
  BookingInquiryChangeLogGroup,
} from '@data/bookingInquiryChangeLog';
import { tradingAgencyOptions } from '@data/tradingAgency';
import { usePatchBookingInquiryLog } from '@hooks/mutation/usePatchBookingInquiryLog';
import { countryStrings } from '@models/Country';
import { BookingInquiryLogField, BookingInquiryLogValue } from '@models/next/BookingInquiryLog';
import { makeMuiStyles, useCommonStyles } from '@styles';
import { formStrings } from '@utils/forms';
import { createStrings } from '@utils/local';
import { formatDateTime } from '@utils/types';
import clsx from 'clsx';

const strings = createStrings({
  ja: {
    beforeValue: '変更前の内容',
    afterValue: '変更後の内容',
  },
});

const useStyles = makeMuiStyles((theme, common) => ({
  changeTitle: {
    ...common.middleText,
    color: theme.palette.common.white,
    padding: 10,
    '&$after': {
      backgroundColor: theme.palette.primary.main,
    },
    '&$before': {
      backgroundColor: theme.palette.grey[500],
    },
  },
  itemContainer: {
    ...common.flexColumn,
    rowGap: 10,
    padding: '2rem',
    paddingTop: '1rem',
  },
  dashedDivider: {
    marginTop: 8,
    borderBottom: `2px dashed ${theme.palette.grey[300]}`,
  },
  managementFieldDisplayContainer: {
    paddingTop: `0 !important`,
    paddingBottom: '0.25rem !important',
  },
  rowTitle: {
    ...common.middleText,
    ...common.fontWeight500,
    ...common.textGray800,
    paddingBottom: '0.25rem !important',
  },
  before: {},
  after: {},
}));

export interface BookingInquiryLogItemProps {
  d2dNo: string;
  item: BookingInquiryChangeLogGroup;
  admin: boolean;
}

type RenderFields = {
  [K in BookingInquiryLogField]: (v: BookingInquiryLogValue[K], before: boolean) => React.ReactNode;
};

const ManagementFieldDisplay: typeof OldManagementFieldDisplay = (props) => {
  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <OldManagementFieldDisplay
      {...props}
      classes={{ container: classes.managementFieldDisplayContainer }}
    />
  );
};

interface BlockFieldDiplayProps {
  label: React.ReactNode;
  value?: React.ReactNode;
  before?: boolean;
}

const BlockFieldDisplay: React.FC<BlockFieldDiplayProps> = (props) => {
  const { label, value, before } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  return (
    <div className={clsx(classes.middleText, classes.textGray800)}>
      <div className={classes.rowTitle}>{label}</div>

      <div
        className={classes.textGray700}
        style={{ textDecoration: before ? 'line-through' : undefined }}
      >
        {value}
      </div>
    </div>
  );
};

export const BookingInquiryLogItem: React.FC<BookingInquiryLogItemProps> = (props) => {
  const { d2dNo, item, admin } = props;

  const classes = {
    ...useCommonStyles(),
    ...useStyles(),
  };

  const [localRead, setLocalRead] = useState(item.hasRead);

  const patchBookingInquiryLog = usePatchBookingInquiryLog(d2dNo, item.groupId);

  const renderFields: RenderFields = {
    shipper: (v, before) => (v ? <ContactDisplay contact={v} stale={before} /> : '-'),
    consignee: (v, before) => (v ? <ContactDisplay contact={v} stale={before} /> : '-'),
    receiver: (v, before) =>
      v ? (
        <BlockFieldDisplay
          label={bookingInquiryStrings.receiver}
          value={<ContactDisplay contact={v} stale={before} />}
        />
      ) : (
        '-'
      ),
    has_pickups: (v, before) => (
      <BlockFieldDisplay
        label={bookingInquiryStrings.collectOption}
        value={v ? bookingInquiryStrings.needsPickup : bookingInquiryStrings.noPickup}
        before={before}
      />
    ),
    cargos: (v, before) => (
      <BlockFieldDisplay
        label={bookingInquiryStrings.cargos}
        value={
          <BookingInquiryCargosDisplay
            cargos={v as any}
            cargosThreshold={999}
            groupThreshold={999}
            variant="inline"
            obsolete={before}
          />
        }
      />
    ),
    trade_condition: (v, before) => (
      <ManagementFieldDisplay
        label={bookingInquiryStrings.tradeCondition}
        value={bookingInquiryTradeConditionStrings[v!] || '-'}
        obsolete={before}
      />
    ),
    customer_no: (v, before) => (
      <ManagementFieldDisplay
        label={bookingInquiryStrings.customerNo}
        value={v || '-'}
        obsolete={before}
      />
    ),
    special_note: (v, before) => (
      <ManagementFieldDisplay
        label={bookingInquiryStrings.specialNote}
        value={v || '-'}
        obsolete={before}
      />
    ),
    use_trading_agency: (v, before) => (
      <ManagementFieldDisplay
        label={bookingInquiryStrings.useTradingAgency}
        value={tradingAgencyOptions.find((o) => o.value === v)?.label || '-'}
        obsolete={before}
      />
    ),
    trading_agency_payer: (v, before) => (
      <ManagementFieldDisplay
        label={bookingInquiryStrings.tradingAgencyPayer}
        value={countryStrings[v!]}
        obsolete={before}
      />
    ),
    use_rcep: (v, before) => (
      <ManagementFieldDisplay
        label={bookingInquiryStrings.useRcep}
        value={v ? formStrings.use : formStrings.notUse}
        obsolete={before}
      />
    ),
    preferred_arrival_date: (v, before) => (
      <BlockFieldDisplay
        label={bookingInquiryStrings.preferredArrivalDate}
        value={v || '-'}
        before={before}
      />
    ),
    pickup_date: (v, before) =>
      v && (
        <BlockFieldDisplay
          label={bookingInquiryStrings.pickupDate}
          value={v || '-'}
          before={before}
        />
      ),
    move_in_date: (v, before) =>
      v && (
        <BlockFieldDisplay
          label={bookingInquiryStrings.moveInDate}
          value={v || '-'}
          before={before}
        />
      ),
    route_plan: (v) =>
      v && (
        <div style={{ padding: '0px 40px' }}>
          {' '}
          <RoutePlanLineDisplay routePlan={v} showDates="all" />
        </div>
      ),
  };

  const renderItem = () => {
    const fields = bookingInquiryAdminGroupNameToFields[item.groupName];

    return (
      <div className={classes.itemContainer}>
        <div className={clsx(classes.changeTitle, classes.after)}>{strings.afterValue}</div>
        <div style={{ height: 0 }} />
        <FormTitle title={bookingInquiryStrings.adminGroupTitle(item.groupName)} gutter={false} />
        {fields.map((field) => {
          const log = item.changeLogs.find((log) => log.edited_field === field);

          if (!log) return null;

          return (
            <Fragment key={field}>
              {(renderFields[field] as any)(log.after_value[field], false)}
            </Fragment>
          );
        })}
        <div className={clsx(classes.changeTitle, classes.before)}>{strings.beforeValue}</div>
        <div style={{ height: 0 }} />
        <FormTitle
          title={bookingInquiryStrings.adminGroupTitle(item.groupName)}
          variant="obsolete"
          gutter={false}
        />
        {fields.map((field) => {
          const log = item.changeLogs.find((log) => log.edited_field === field);

          if (!log) return null;

          return (
            <Fragment key={field}>
              {(renderFields[field] as any)(log.before_value[field], true)}
            </Fragment>
          );
        })}
      </div>
    );
  };

  return (
    <NotificationItem
      title={`${bookingInquiryStrings.adminGroupTitle(item.groupName)}の変更`}
      date={formatDateTime(item.createdAt)}
      hasRead={item.hasRead || localRead}
      onRead={() => {
        if (!admin) {
          setLocalRead(true);
          patchBookingInquiryLog.mutate();
        }
      }}
    >
      {renderItem()}
    </NotificationItem>
  );
};
